import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCommitteeData, getKhidmatGuzarData } from '../../prefetchNames';

export default function Umoor() {
  const [hoveredUmoorCard, setHoveredUmoorCard] = useState(null);
  const [umoorData, setUmoorData] = useState({});
  const [khidmatGuzarData, setKhidmatGuzarData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadCommitteeData() {
      try {
        const [committeeData, khidmatData] = await Promise.all([
          getCommitteeData(),
          getKhidmatGuzarData(),
        ]);

        setUmoorData(committeeData.umoor || {});
        setKhidmatGuzarData(khidmatData || {});
      } catch (error) {
        console.error('Failed to load committee data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadCommitteeData();
  }, []);

  const scrollToUmoor = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const normalizeKey = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const getKhidmatContact = (name, manualEmail = '') => {
    const matchedEntry = Object.entries(khidmatGuzarData).find(([orgName]) =>
      normalizeKey(orgName) === normalizeKey(name)
    );

    if (!matchedEntry) {
      return {
        type: 'secretary-email',
        secretary: '',
        email: manualEmail,
      };
    }

    const [, details] = matchedEntry;

    return {
      type: 'secretary-email',
      secretary: details.secretary || '',
      email: manualEmail || '',
    };
  };

  const renderContact = (contact) => {
    if (!contact) return <span className="text-gray-400">—</span>;

    if (contact.type === 'tadfeen-link') {
      return (
        <Link
          to="/committees/tadfeen"
          className="text-dawoodi-gold hover:underline font-semibold"
        >
          See Tadfeen Committee page
        </Link>
      );
    }

    if (contact.type === 'email') {
      return (
        <a
          href={`mailto:${contact.value}`}
          className="text-dawoodi-gold hover:underline"
        >
          {contact.value}
        </a>
      );
    }

    if (contact.type === 'phone-email') {
      return (
        <div className="space-y-1">
          <a href={`tel:${contact.phone}`} className="block text-dawoodi-gold hover:underline">
            {contact.phone}
          </a>
          <a href={`mailto:${contact.email}`} className="block text-dawoodi-gold hover:underline">
            {contact.email}
          </a>
        </div>
      );
    }

    if (contact.type === 'secretary-email') {
      return (
        <div className="space-y-1">
          {contact.secretary ? (
            <p className="text-gray-700">{contact.secretary}</p>
          ) : (
            <p className="text-gray-400">Secretary information unavailable</p>
          )}

          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="block text-dawoodi-gold hover:underline"
            >
              {contact.email}
            </a>
          )}
        </div>
      );
    }

    return <span>{contact.value}</span>;
  };

  const renderSubcommitteeName = (subcommittee) => {
    const linkedClass =
      'text-dawoodi-gold hover:text-dawoodi-navy font-semibold hover:underline transition-colors';

    if (subcommittee.linkType === 'internal') {
      return (
        <Link to={subcommittee.link} className={linkedClass}>
          {subcommittee.name}
        </Link>
      );
    }

    if (subcommittee.linkType === 'external') {
      return (
        <a
          href={subcommittee.link}
          target="_blank"
          rel="noopener noreferrer"
          className={linkedClass}
        >
          {subcommittee.name}
        </a>
      );
    }

    return <span className="font-semibold text-dawoodi-navy">{subcommittee.name}</span>;
  };

  const umoorList = [
    {
      id: 'umoor-deeniyah',
      name: 'Umoor Deeniyah',
      title: 'Religious Affairs',
      logo: '/umoor_deeniyah_logo.png',
      description:
        'Umoor Deeniyah works to strengthen our connection with Shariat Mohammediyah by ensuring it is practiced correctly in daily life, and by guiding the Jamaat’s nizaam to remain aligned with its principles.',
      contact: { type: 'email', value: 'deeniyah@njjamaat.org' },
      subcommittees: [
        { name: 'Zakereen', contact: { type: 'email', value: 'zakereen@njjamaat.org' } },
        {
          name: 'Taisser al-Nikah',
          contact: { type: 'email', value: 'tnc-nj@njjamaat.org' },
        },
        {
          name: 'Tahjeez Takfeen',
          linkType: 'internal',
          link: '/resources/cemetery',
          contact: { type: 'tadfeen-link' },
        },
      ],
    },
    {
      id: 'umoor-talimiyah',
      name: 'Umoor Taalimiyah',
      title: 'Education',
      logo: '/umoor_taalimiyah_logo.png',
      description:
        'Umoor Talimiyah nurtures a culture of learning by encouraging hifz of the Quran in every household and supporting Mumineen in achieving excellence in both deeni and dunyawi education.',
      contact: { type: 'email', value: 'umoor.talimiyah@njjamaat.org' },
      subcommittees: [
        { name: 'Asbaaq',
          contact: { type: 'email', value: 'umoor.talimiyah@njjamaat.org' },
         },
        {
          name: 'Madrasatul Burhaniyah',
          contact: { type: 'email', value: 'mbnj.admin@njjamaat.org' },
        },
        {
          name: 'Qismal Tahfeez',
          contact: { type: 'email', value: 'qismaltahfeez@njjamaat.org' },
        },
      ],
    },
    {
      id: 'umoor-marafiq',
      name: 'Umoor Marafiq Burhaniyah',
      title: 'Welfare',
      logo: '/umoor_marafiq_burhaniyah.png',
      description:
        'Umoor Marafiq Burhaniyah works to enhance the quality of life for every Mumin by supporting their growth and well-being in deen, education, nutrition, health, housing, and livelihood.',
      contact: { type: 'email', value: 'marafiq@njjamaat.org' },
      subcommittees: [],
    },
    {
      id: 'umoor-maliyah',
      name: 'Umoor Maaliyah',
      title: 'Budgeting & Accounts',
      logo: '/umoor_maliyah.png',
      description:
        'Umoor Maliyah promotes responsible financial management by ensuring the Jamaat’s accounts and budgets align with modern requirements and regulations, while encouraging effective home budgeting and supporting structured financial planning for community development.',
      contact: { type: 'email', value: 'umoor.maliyah@njjamaat.org' },
      subcommittees: [
        {
          name: 'Jamaat Office',
          contact: {
            type: 'phone-email',
            phone: '732-390-4214',
            email: 'jamaatoffice@anjuman-e-burhani.org',
          },
        },
      ],
    },
    {
      id: 'umoor-mawarid',
      name: 'Umoor Mawarid Bashariyah',
      title: 'Human Resources',
      logo: '/umoor_mawarid_bashariyah.png',
      contact: { type: 'email', value: 'hr@njjamaat.org' },

      description:
        'Umoor Mawarid Bashariyah focuses on developing and empowering Mumineen and Muminaat by nurturing their skills and preparing them to actively participate in the khidmat of Dawat and the Jamaat.',
        subcommittees: [
          {
            name: 'Burhani Guards (BGI)',
            contact: getKhidmatContact('Burhani Guards (BGI)'),
          },
          {
            name: 'Shabab Eid iz Zahabi (SEZ)',
            contact: getKhidmatContact('Shabab Eid iz Zahabi (SEZ)', 'sez@njjamaat.org'),
          },
          {
            name: 'Toloba',
            linkType: 'external',
            link: 'https://www.tolobanj.org',
            contact: getKhidmatContact('Toloba', 'toloba@njjamaat.org'),
          },
          {
            name: "Saifee Women's (SWA)",
            contact: getKhidmatContact("Saifee Women's (SWA)", 'saifeewomens@njjamaat.org'),
          },
          {
            name: "Burhani Women's (BWA)",
            contact: getKhidmatContact("Burhani Women's (BWA)", 'bwa@njjamaat.org'),
          },
          {
            name: 'Bunaiyyat Eid iz Zahabi (BEZ)',
            contact: getKhidmatContact('Bunaiyyat Eid iz Zahabi (BEZ)', 'buniyaat@njjamaat.org'),
          },
          {
            name: 'Taalebaat',
            contact: getKhidmatContact('Taalebaat', 'talebaat@njjamaat.org'),
          },
        ],
    },
    {
      id: 'umoor-dakheliyah',
      name: 'Umoor Dakheliyah',
      title: 'Internal Affairs',
      logo: '/umoor_dakheliyah.png',
      description:
        'Umoor Dakheliyah works to maintain accurate and up-to-date data of Mumineen through effective use of technology, enabling better planning, coordination, and overall community well-being.',
      contact: { type: 'email', value: 'umoor.dakheliyah@njjamaat.org' },
      subcommittees: [],
    },
    {
      id: 'umoor-kharjiyah',
      name: 'Umoor Kharjiyah',
      title: 'Public Relations',
      logo: '/umoor_kharejiyah.png',
      description:
        'Umoor Kharejiyah fosters positive relationships with the wider community by promoting harmony, mutual respect, and meaningful engagement in every city.',
      contact: { type: 'email', value: 'umoor.kharjiyah@njjamaat.org' },
      subcommittees: [],
    },
    {
      id: 'umoor-qaza',
      name: 'Umoor Qaza',
      title: 'Legal Affairs',
      logo: '/umoor_khazah.png',
      description:
        'Umoor al-Qaza encourages resolving disputes with understanding, fairness, and mutual respect, promoting amicable solutions within the Jamaat while ensuring matters are handled in accordance with the nizaam.',
      contact: { type: 'email', value: 'umoor.qaza@njjamaat.org' },
      subcommittees: [],
    },
    {
      id: 'umoor-fmb',
      name: 'Umoor FMB',
      title: 'Faiz al Mawaid Al Burhaniyah',
      logo: '/umoor_fmb.png',
      contact: { type: 'email', value: 'mawaid.nj@njjamaat.org' },

      description:
        'Umoor Faizul Mawaidil Burhaniyah ensures that every household receives nutritious, well-prepared meals, while promoting consistency in quality, adequate portions, and the avoidance of food wastage.',
      subcommittees: [],
    },
    {
      id: 'umoor-iqtesadiyah',
      name: 'Umoor Iqtesadiyah',
      title: 'Finance & Business Development',
      logo: '/umoor_iqtesadyah.png',
      description:
        'Umoor Iqtesadiyah supports ethical and sustainable economic growth by guiding Mumineen to conduct business in accordance with Shariat, build financial stability, and benefit from systems such as Qardan Hasana.',
      subcommittees: [
        { name: 'Tijaarat Raabehah',
          contact: { type: 'email', value: 'al-tijaarat@njjamaat.org' },
         },
        {
          name: 'Qardan Hasana',
          linkType: 'internal',
          link: '/resources/qardan-hasanah',
          contact: { type: 'email', value: 'qh@njjamaat.org' },
        },
      ],
    },
    {
      id: 'umoor-amlak',
      name: 'Umoor Amlaak',
      title: 'Waqf & Trust',
      logo: '/umoor_amlak.png',
      description:
        'Umoor Amlaak ensures that Dawat properties are properly maintained and effectively utilized, so that they continue to serve and benefit Mumineen.',
      contact: { type: 'email', value: 'amlaak@njjamaat.org' },
      subcommittees: [],
    },
    {
      id: 'umoor-sehhat',
      name: 'Umoor Sehat',
      title: 'Health',
      logo: '/umoor_sehat.png',
      description:
        'Umoor Sehhat promotes the overall well-being of Mumineen by encouraging care for both physical and spiritual health, with a strong focus on preventive healthcare and healthy living.',
      contact: { type: 'email', value: 'umoor.sehat@njjamaat.org' },
      subcommittees: [],
    },
  ].map((umoor) => ({
    ...umoor,
    coordinator: umoorData[umoor.name]?.coordinator || '',
    phone: umoorData[umoor.name]?.phone || '',
  }));

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div
        className="relative py-20 text-white overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d66 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full border-2 border-dawoodi-gold/30"></div>
          <div className="absolute bottom-10 left-20 w-48 h-48 rounded-full border-2 border-dawoodi-gold/30"></div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 animate-pulse">
          <div className="flex items-center justify-center mb-6">
            <p className="font-poppins text-sm tracking-wide text-gray-500 uppercase animate-pulse">
              Loading...
            </p>
          </div>

          <div className="h-10 w-48 bg-gray-200 rounded mb-10"></div>

          <div className="space-y-5">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="h-40 bg-gray-100 border-2 border-dawoodi-gold/10 rounded-xl"
              ></div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div
        className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 transition-opacity duration-500 ${
          isLoading ? 'opacity-0 hidden' : 'opacity-100'
        }`}
      >
        <div className="mb-10">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-1 h-8 bg-gradient-to-b from-dawoodi-navy to-transparent rounded-full"></div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-dawoodi-navy">
              12 Umoor Committees
            </h2>
          </div>

          <p className="font-poppins text-gray-600 max-w-3xl">
            Select an Umoor below to jump directly to its coordinator and related subcommittees.
          </p>
        </div>

        {/* Quick Jump */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-12">
          {umoorList.map((umoor) => (
            <button
              key={umoor.id}
              onClick={() => scrollToUmoor(umoor.id)}
              className="text-left bg-white border-2 border-dawoodi-gold/20 hover:border-dawoodi-gold hover:shadow-md rounded-xl px-4 py-3 transition-all duration-200"
            >
              <p className="font-playfair text-sm font-bold text-dawoodi-navy leading-tight">
                {umoor.name}
              </p>
              <p className="font-poppins text-xs text-dawoodi-gold mt-1">
                {umoor.title}
              </p>
            </button>
          ))}
        </div>

        <div className="space-y-7">
          {umoorList.map((umoor, index) => (
            <div
              id={umoor.id}
              key={umoor.id}
              onMouseEnter={() => setHoveredUmoorCard(index)}
              onMouseLeave={() => setHoveredUmoorCard(null)}
              className={`scroll-mt-28 group relative transition-all duration-300 ${
                index % 2 === 1 ? 'lg:ml-6' : 'lg:mr-6'
              } ${hoveredUmoorCard === index ? '-translate-y-1' : ''}`}
            >
              <div
                className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
                  hoveredUmoorCard === index ? 'bg-dawoodi-gold/10 blur-lg opacity-100' : 'opacity-0'
                }`}
              ></div>

              <div
                className={`relative bg-white border-2 rounded-xl overflow-hidden transition-all duration-300 ${
                  hoveredUmoorCard === index
                    ? 'border-dawoodi-gold shadow-2xl'
                    : 'border-dawoodi-gold/20 shadow-lg'
                }`}
              >
                <div className="h-1 bg-gradient-to-r from-dawoodi-navy via-dawoodi-gold to-transparent"></div>

                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-playfair font-bold text-base text-dawoodi-navy">
                        {umoor.name}
                      </h3>

                      <p className="font-poppins text-xs text-dawoodi-gold font-semibold mt-1">
                        {umoor.title}
                      </p>

                      <p className="font-poppins text-sm text-gray-600 mt-4 leading-relaxed">
                        {umoor.description}
                      </p>

                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <p>
                          <strong>Coordinator:</strong>{' '}
                          {umoor.coordinator || <span className="text-gray-400">—</span>}
                        </p>

                        <p>
                          <strong>Phone:</strong>{' '}
                          {umoor.phone ? (
                            <a
                              href={`tel:${umoor.phone}`}
                              className="text-dawoodi-gold hover:underline"
                            >
                              {umoor.phone}
                            </a>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </p>

                        <p className="sm:col-span-2">
                          <strong>Email:</strong>{' '}
                          {renderContact(umoor.contact)}
                        </p>
                      </div>
                    </div>

                    <img
                      src={umoor.logo}
                      alt={umoor.name}
                      className="w-20 h-20 object-contain flex-shrink-0"
                    />
                  </div>

                  {umoor.subcommittees.length > 0 && (
                    <div className="mt-6 overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-dawoodi-light/50 text-left">
                            <th className="px-4 py-3 font-poppins text-dawoodi-navy border border-dawoodi-gold/20">
                              {umoor.subcommittees.length === 1 ? 'Subcommittee:' : 'Subcommittees:'}
                            </th>
                            <th className="px-4 py-3 font-poppins text-dawoodi-navy border border-dawoodi-gold/20">
                              Contact
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {umoor.subcommittees.map((subcommittee, subIndex) => (
                            <tr key={subIndex} className="hover:bg-dawoodi-light/30">
                              <td className="px-4 py-3 border border-dawoodi-gold/20 align-top">
                                {renderSubcommitteeName(subcommittee)}
                              </td>
                              <td className="px-4 py-3 border border-dawoodi-gold/20 align-top">
                                {renderContact(subcommittee.contact)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}